const DESIGN_H = 663;   // Design-Höhe der Collage — muss mit $hs-design-h im SCSS übereinstimmen
const MOBILE_BP = 1024;

// ── Headline-Effekt (Overlap-Wipe + Parallax, Figma-Rekonstruktion) ──────────
// Die Headline hängt rechts über die Bildkante in die schwarze Lücke. Der Teil
// ÜBER dem Bild ist farbig, der überhängende Teil weiß. Der Parallax-Drift
// schiebt die Headline beim Vorwärtsscrollen von links nach rechts relativ zum
// Bild (rückwärts umgekehrt) → die Farbgrenze wandert mit: farbig einfahren,
// ins Weiße auswischen. Kein Einrasten — die Sektion scrollt komplett frei.
const OVERHANG = 0.5;          // Anteil der Headline-Breite, der im Ruhezustand über die Bildkante hängt
                               // (im Scroll-Modus zusätzlich auf --hl-clear gekappt, s. measureHeads)
const PARALLAX_AMP_FRAC = 0.65; // Parallax-Amplitude als Anteil der Headline-Breite (Design-px)
const MIN_ON_IMAGE_FRAC = 0.15; // Mindest-Überlapp mit dem Bild — die Headline steht NIE komplett daneben

export function initHorizontalScrolling() {
  const sections = document.querySelectorAll('.js-horizontalScrolling');
  if (!sections.length) return;

  const supportsSDA = typeof CSS !== 'undefined' && CSS.supports('animation-timeline: scroll()');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  sections.forEach(section => {
    const stage = section.querySelector('.horizontalScrolling__stage');
    if (!stage) return;

    const scroller = section.querySelector('.horizontalScrolling__pin'); // Mobile/Fallback: horizontaler Scroll-Container
    const track = section.querySelector('.horizontalScrolling__track');

    // Scroll-Hinweis: nach der ersten horizontalen Wischgeste ausblenden.
    // Im Pinned-Modus feuert __pin kein scroll-Event (overflow: clip) und der
    // Hinweis ist per CSS display: none — kein Guard nötig.
    const hint = section.querySelector('.js-horizontalScrollingHint');
    if (hint && scroller) {
      scroller.addEventListener(
        'scroll',
        () => hint.classList.add('is-hidden'),
        { passive: true, once: true }
      );
    }

    const panels = Array.from(stage.querySelectorAll('.horizontalScrolling__panel'));

    // Feature-Headlines mit ihrer gecachten Geometrie (in layout() befüllt).
    // { el, mediaCenterStage, onImage, w } — Layout-px im Stage-Koordinatenraum.
    let heads = [];

    let travel = 0;
    let scale = 1;
    let pinned = false;

    // Layout: Skalierung + Modus + Headline-Überhang festlegen
    function layout() {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const isMobile = vw < MOBILE_BP;
      // Pinned auch OHNE SDA-Support (z. B. Firefox): dann setzt update() den
      // Track-translateX pro rAF-Frame selbst statt der CSS scroll-driven animation.
      pinned = !reduceMotion && !isMobile;

      section.classList.toggle('is-pinned', pinned);
      section.classList.toggle('is-scroll', !pinned);
      if (!pinned && track) track.style.removeProperty('transform');

      if (pinned) {
        // Pinned: so hoch wie der Viewport, aber NIE höher als die Design-Höhe (scale ≤ 1).
        scale = Math.min(vh, DESIGN_H) / DESIGN_H;
      } else {
        // Scroll-Modus (Mobile/Fallback): eigenes responsives CSS-Layout, KEINE
        // Skalierung — Texte rendern in normaler Lesegröße, die Wipe-Mathematik
        // (measureHeads/applyHeadlineFx) läuft in echten Layout-Pixeln.
        scale = 1;
      }

      // Intrinsische Breite der Collage messen (Layout-px, ignoriert transform: scale).
      const stageW = stage.scrollWidth;
      const trackW = stageW * scale;
      travel = Math.max(0, trackW - vw);

      section.style.setProperty('--scale', String(scale));
      section.style.setProperty('--track-w', trackW + 'px');
      section.style.setProperty('--travel', travel + 'px');

      measureHeads();
    }

    // Pro Feature-Headline: Geometrie messen und den garantierten Überhang setzen.
    // Wichtig: --hl-left ist PANEL-relativ (Headline ist position:absolute im Panel).
    // Für die Parallax-Phase brauchen wir zusätzlich die Stage-absolute Position.
    function measureHeads() {
      heads = [];
      const vw = window.innerWidth;
      panels.forEach(panel => {
        const el = panel.querySelector('.horizontalScrolling__headline');
        if (!el) return;
        const media = panel.querySelector('.horizontalScrolling__media');
        // Überhang temporär neutralisieren, damit offsetWidth die echte Textbreite misst.
        el.style.removeProperty('--hl-left');
        const w = el.offsetWidth;
        const mediaW = media ? media.offsetWidth : panel.offsetWidth;
        // Scroll-Modus: Überhang auf die reservierte Zone kappen (--hl-clear =
        // padding-right des Panels = offsetWidth − mediaW) — sonst ragt die
        // Headline der LETZTEN Szene über das Stage-Ende hinaus und ist per
        // Scroll nie erreichbar (abgeschnitten).
        const maxOverhang = pinned ? Infinity : Math.max(0, panel.offsetWidth - mediaW);
        const overhang = Math.min(OVERHANG * w, maxOverhang);
        const onImage = w - overhang;              // Teil, der im Ruhezustand auf dem Bild liegt
        const hlLeftPanel = mediaW - onImage;      // panel-relativ: hängt um OVERHANG·w über die Bildkante
        el.style.setProperty('--hl-left', hlLeftPanel + 'px');
        // Transit-Referenz ist die BILDmitte (nicht die Headline-Mitte): steht die
        // Szene eingerastet mittig, ist u ≈ 0 → kein Drift im Ruhezustand → die
        // Headline bleibt exakt im reservierten Overhang (wichtig auf Mobile, sonst
        // schiebt der Parallax sie aus dem Viewport).
        const mediaCenterStage = panel.offsetLeft + mediaW / 2;
        // Phasen-Offset für Szenen, die schon in der Ausgangslage (Pin-Start bzw.
        // scrollLeft = 0) links der Viewport-Mitte stehen (v. a. die ERSTE Szene):
        // ohne Kompensation wäre deren Headline von Anfang an weit nach rechts
        // gedriftet. u0 verschiebt die Transit-Phase so, dass die Ausgangslage als
        // Ruhelage zählt (u = 0 → kein Drift, voller Overhang auf dem Bild).
        const u0 = Math.min(0, (2 * mediaCenterStage * scale) / vw - 1);
        heads.push({ el, mediaCenterStage, onImage, w, u0 });
      });
    }

    // Reveal + Parallax für alle Headlines, ausgehend vom Bildschirm-x des Stage-Ursprungs.
    // originX kürzt den jeweiligen Scroll-Mechanismus (Pin-Translate bzw. scrollLeft) heraus.
    function applyHeadlineFx(originX) {
      const vw = window.innerWidth;
      heads.forEach(h => {
        // Bildschirm-Mitte des BILDES → Transit-Phase u (0 = Szene steht mittig).
        const centerX = originX + h.mediaCenterStage * scale;
        // +1 rein … 0 Mitte/Ruhelage … −1 raus; u0 verschiebt die Phase für Szenen,
        // deren Ausgangslage links der Mitte liegt (erste Szene → startet in Ruhe).
        const u = Math.min(1, Math.max(-1, (2 * centerX) / vw - 1 - h.u0));
        // Negatives Vorzeichen: beim Vorwärtsscrollen (u: +1 → −1) drift die
        // Headline von links nach rechts relativ zum Bild, rückwärts umgekehrt.
        // Nach rechts gekappt: mindestens MIN_ON_IMAGE_FRAC·w bleiben auf dem Bild —
        // ab da „dockt" die Headline an der Bildkante an und fährt mit ihr mit.
        const parLocal = Math.min(
          -PARALLAX_AMP_FRAC * h.w * u,
          h.onImage - MIN_ON_IMAGE_FRAC * h.w
        ); // Design-px, Drift relativ zum Bild
        // Overlap mit dem Bild (panel-relativ): Ruhewert onImage, per Parallax verschoben.
        const reveal = Math.min(1, Math.max(0, (h.onImage - parLocal) / h.w));
        h.el.style.translate = parLocal + 'px';
        h.el.style.setProperty('--reveal', String(reveal));
      });
    }

    function setStaticLook() {
      heads.forEach(h => {
        h.el.style.removeProperty('translate');
        h.el.style.setProperty('--reveal', '1'); // statischer finaler Look
      });
    }

    // Pro Frame analytisch aus transformFREIEN Bezugsgrößen (Section-Rect bzw. scrollLeft)
    // — kein Auslesen SDA-bewegter Kinder (deren Rects können stale sein).
    function update() {
      if (reduceMotion) { setStaticLook(); return; }
      if (pinned) {
        if (travel <= 0) {
          if (track && !supportsSDA) track.style.removeProperty('transform');
          setStaticLook();
          return;
        }
        const rect = section.getBoundingClientRect();
        const p = Math.min(1, Math.max(0, -rect.top / travel));
        // JS-Pin-Fallback ohne scroll-driven animations (z. B. Firefox):
        // dieselbe Bewegung, nur schreibt die JS den translateX selbst.
        if (track && !supportsSDA) track.style.transform = 'translateX(' + (-p * travel) + 'px)';
        applyHeadlineFx(rect.left - p * travel);
      } else if (scroller) {
        // Mobile/Fallback: der Wipe läuft auch hier — getrieben vom horizontalen scrollLeft.
        applyHeadlineFx(scroller.getBoundingClientRect().left - scroller.scrollLeft);
      } else {
        setStaticLook();
      }
    }

    // rAF-Loop nur während die Sektion sichtbar ist
    let running = false;
    function loop() {
      if (!running) return;
      update();
      requestAnimationFrame(loop);
    }

    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting && !running) {
          running = true;
          requestAnimationFrame(loop);
        } else if (!e.isIntersecting) {
          running = false;
        }
      });
    }, { rootMargin: '200px' });

    layout();
    update();
    io.observe(section);

    // Resize: Layout + Reveal neu berechnen (rAF-gedrosselt)
    let resizeRaf;
    window.addEventListener('resize', () => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => { layout(); update(); });
    });
  });
}
